import React from 'react';
import { Check } from 'lucide-react';

type CheckboxProps = {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: React.ReactNode;
  disabled?: boolean;
  className?: string;
  size?: 'sm' | 'md';
};

/** Accessible custom checkbox used across forms. */
export const Checkbox: React.FC<CheckboxProps> = ({
  checked,
  onChange,
  label,
  disabled = false,
  className = '',
  size = 'md',
}) => {
  const box = size === 'sm' ? 'h-4 w-4 rounded' : 'h-5 w-5 rounded-md';
  const icon = size === 'sm' ? 'h-2.5 w-2.5' : 'h-3.5 w-3.5';
  const text = size === 'sm' ? 'text-xs' : 'text-sm';

  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={`inline-flex items-center gap-2.5 text-left group disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
    >
      <span
        className={`relative flex ${box} shrink-0 items-center justify-center border-2 transition-all duration-150 ${
          checked
            ? 'border-emerald-500 bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-sm shadow-emerald-500/25'
            : 'border-stone-300 dark:border-slate-500 bg-white dark:bg-slate-800 group-hover:border-emerald-400 dark:group-hover:border-emerald-500'
        }`}
      >
        {checked ? <Check className={icon} strokeWidth={3} /> : null}
      </span>
      <span className={`${text} text-stone-700 dark:text-slate-300 leading-snug group-hover:text-stone-900 dark:group-hover:text-white transition-colors`}>
        {label}
      </span>
    </button>
  );
};
